import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { Collapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdrTentativeAgreementModalV3 from "./AdrTentativeAgreementModalV3";
import AdrTentativeAgreementSelectionModalV3 from "./AdrTentativeAgreementSelectionModalV3";
import adrActions from "../../_actions/adrActions";
import { actionScripts } from "../../_helpers/actionScripts";
import { ADR_EDIT } from "../../_constants/actionConstants";
import { SplitDropdown } from "../_common/SplitDropdown";
import cem4907PdfActions from "../../_actions/cem4907PdfActions";
import AlertModal from "../_common/AlertModal";

export default function AdrReviewTentativeAgreementsV3(props) {
  const { tabId, projectId, markAdrDirty, markAdrClean, status } = props;

  const [showSection, _toggleSection] = useState(false);
  const [showTaSelectionModal, _toggleTaSelectionModal] = useState(false);
  const [showTaModal, _toggleTaModal] = useState(false);
  const [alertState_deleteConfirm, _setAlertState_deleteConfirm] = useState({
    showAlert: false,
    data: null,
  });
  const toggleAlert_deleteConfirm = () =>
    _setAlertState_deleteConfirm({
      ...alertState_deleteConfirm,
      showAlert: !alertState_deleteConfirm.showAlert,
    });

  const [clientId, setClientId] = useState({ value: 0, isNew: false });

  const notInitialRender = useRef(false);
  const dispatch = useDispatch();
  const adr = useSelector((state) => state.adr.openAdrs[tabId] || null);
  const sessionData = useSelector((state) =>
    state.adr.openAdrs[tabId] ? state.adr.openAdrs[tabId].sessionData || null : null
  );
  const projContractorList = useSelector(
    (state) => state.contractors.projContractorLists[projectId] || null
  );

  useEffect(() => {
    if (notInitialRender.current) {
      // If clientId is being updated as a result of adding a new TA thru TaSelectionModal,
      // close the TaSelectionModal.
      if (showTaSelectionModal) toggleTaSelectionModal();
      toggleTaModal();
    } else notInitialRender.current = true;
  }, [clientId]);

  useEffect(
    () =>
      sessionData &&
      sessionData.newTaClientId &&
      setClientId({ value: sessionData.newTaClientId, isNew: true }),
    [sessionData]
  );

  useEffect(() => {
    if (adr && adr.tentativeAgreements && adr.tentativeAgreements.length > 0) {
      _toggleSection(true);
    }
  }, [adr]);

  const taList = useMemo(() => {
    if (adr && adr.tentativeAgreements && adr.tentativeAgreements.length > 0)
      return _.orderBy(adr.tentativeAgreements, "createdOn", "asc");
  }, [adr]);

  const handleAlert_deleteConfirm = useCallback(
    (actionType) => async (e) => {
      e.preventDefault();
      toggleAlert_deleteConfirm();
      if (actionType === "accept" && alertState_deleteConfirm.data) {
        // Delete all existing attachments
        const filePathsToDelete = (alertState_deleteConfirm.data.attachments || [])
          .map((taa) => [taa.filePath, taa.imageThumbnailFilePath])
          .flat();
        dispatch(adrActions.deleteAttachments(filePathsToDelete));
        dispatch({
          type: ADR_EDIT.TENTATIVE_AGREEMENTS.DELETE,
          tabId,
          clientId: alertState_deleteConfirm.data.clientId,
        });
        await dispatch(adrActions.save(tabId, status.isNew));
        actionScripts.REFETCH_ADR_IN_TAB.run(
          projectId,
          adr.assistantDailyReportId,
          tabId
        );
        // Saved, so mark as clean
        markAdrClean();
      }
    },
    [alertState_deleteConfirm]
  );

  /**
   *
   * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
   * @param {Object.<number, number[]>} taSequences - keys are wpTabIds, values are arrays of ta.clientIds
   * @returns
   */
  const renderTa = (ta, taSequences) => {
    const isDisputed =
      ta.isDisputedByCaltransRep ||
      Object.values(ta.contractorsClient).some((cc) => cc.isDisputedByContractor);
    const isCaltransSigned = !isDisputed && ta.caltransRepSignedOn;
    const isContractorSigned =
      !isDisputed &&
      Object.values(ta.contractorsClient).some((cc) => cc.contractorRepSignedOn);
    const partialSigned =
      (isCaltransSigned && !isContractorSigned) ||
      (!isCaltransSigned && isContractorSigned);
    const bothSigned = isCaltransSigned && isContractorSigned;

    const labelBase =
      adr && adr.workPerformedClient[ta.wpTabId]
        ? adr.workPerformedClient[ta.wpTabId].wpShortCaption
        : "(Item/CO Desc. N/A)";
    const labelSeq =
      ta.wpTabId in taSequences && taSequences[ta.wpTabId].length > 1
        ? taSequences[ta.wpTabId].length
        : 0;
    const ctrId = Object.keys(ta.contractorsClient).length
      ? Object.keys(ta.contractorsClient)[0]
      : 0;
    const ctr = (projContractorList || []).find((c) => c.contractorId === +ctrId);
    const labelCtr = `${ctr ? ctr.name : "Ctr. N/A"}`;
    const labelSuffix = isDisputed
      ? "Disputed"
      : partialSigned
      ? "1/2 Signed"
      : bothSigned
      ? "2/2 Signed"
      : "Pending";
    const label = `${labelBase}${labelSeq ? ` (${labelSeq})` : ""} (${labelCtr})`;
    return (
      <SplitDropdown
        key={`taLinkKey_${ta.clientId}`}
        id={`ta_${ta.clientId}`}
        onClick={handleClick_ta(ta)}
        className="ta-list__item"
        color={
          isDisputed ? "warning" : partialSigned || bothSigned ? "success" : "secondary"
        }
        icon={partialSigned || bothSigned ? "check" : null}
        label={
          <span>
            <b>{label}</b> {`(${labelSuffix})`}
          </span>
        }
        menu={createDropdownMenu(ta)}
      />
    );
  };

  const renderContent = () => {
    // Track any multiple occurrences of TA for same WorkPerformed.
    /** @type {Object.<number, number[]>} */
    let taSequences = {}; // Keys are wpTabIds, values are clientIds for TA
    return (
      <div className="card">
        <div className="card-body p-2">
          <div className="flex flex-row flex-wrap ta-list">
            {(taList || []).length > 0
              ? taList.map((ta) => {
                  taSequences[ta.wpTabId] = [
                    ...(ta.wpTabId in taSequences ? taSequences[ta.wpTabId] : []),
                    ta.clientId,
                  ];
                  return renderTa(ta, taSequences);
                })
              : "You don't have any tentative agreements for this day."}
          </div>
        </div>
      </div>
    );
  };

  const handleClick_ta = (ta) => async () => {
    // Save changes before accessing TA.
    if (adr.isDraft || adr.isRejected) {
      if (status.isChanged) {
        dispatch(adrActions.clearTempFilePaths(tabId));
        await dispatch(adrActions.save(tabId, status.isNew));
        await actionScripts.REFETCH_ADR_IN_TAB.run(
          projectId,
          adr.assistantDailyReportId,
          tabId
        );
        // Saved, so mark as clean
        markAdrClean();
      }
    }
    setClientId({ value: ta.clientId, isNew: false });
  };

  const handleClick_add = async (e) => {
    e.preventDefault();
    if (adr.isDraft || adr.isRejected) {
      const { projectId, tabId } = props;
      dispatch(adrActions.clearTempFilePaths(tabId));
      const adrId = await dispatch(adrActions.save(tabId, status.isNew));
      await actionScripts.REFETCH_ADR_IN_TAB.run(projectId, adrId, tabId);
      toggleTaSelectionModal();
    }
  };

  const toggleSection = () => _toggleSection(!showSection);
  const toggleTaSelectionModal = () => _toggleTaSelectionModal(!showTaSelectionModal);
  const toggleTaModal = () => _toggleTaModal(!showTaModal);

  const createDropdownMenu = useCallback((ta) => {
    let menu = [
      {
        label: "Open",
        icon: "folder-open",
        onClick: handleClick_ta(ta),
        show: true,
      },
      {
        label: "View TA (PDF)",
        icon: "file-pdf",
        onClick: () => dispatch(cem4907PdfActions.requestFromDb(ta.tentativeAgreementId)),
        show: true,
      },
    ];
    if (adr.isDraft || adr.isRejected)
      menu.push({
        label: <span className="text-danger font-weight-bold">Delete</span>,
        icon: "trash-alt",
        iconClass: "text-danger",
        onClick: () => _setAlertState_deleteConfirm({ showAlert: true, data: ta }),
        show: true,
      });
    return menu;
  }, []);

  /**
   * Once a workPerformed is selected, we must close the selection modal
   * and transition to the edit modal.
   * This is called ONLY when creating a new TA.
   * @param {number} wpTabId
   */
  const createTa = (wpTabId, contractorId) =>
    dispatch({
      type: ADR_EDIT.TENTATIVE_AGREEMENTS.ADD_BLANK,
      tabId,
      wpTabId,
      contractorId,
    });

  return (
    <div className="card m-2">
      <div className="card-body">
        <p className={`${showSection ? "mb-2" : "mb-0"}`}>
          <span onClick={toggleSection} className="h3 pointer-cursor">
            {showSection ? (
              <FontAwesomeIcon icon="caret-down" className="fa-fw mr-2" />
            ) : (
              <FontAwesomeIcon icon="caret-right" className="fa-fw mr-2" />
            )}
            Tentative Agreements
          </span>
          <span className="h4 badge badge-secondary ml-2 align-middle">
            {adr && adr.tentativeAgreements ? adr.tentativeAgreements.length : "0"}
          </span>
        </p>
        <Collapse isOpen={showSection}>
          <div className="alert alert-info" role="alert">
            <h5>
              <FontAwesomeIcon icon="info-circle" className="mr-2" />
              Tentative Agreement Format
            </h5>
            You can view or edit tentative agreements in either: 1) ProDMS-provided input
            screen, which outputs to CEM-4907, or 2) contractor-provided custom form (for
            which you can upload a scan or photo with additional metadata).
          </div>
          {renderContent()}
          <div className="d-flex flex-row mt-2">
            <button
              type="button"
              id="addTa"
              name="addTa"
              className="btn btn-secondary mr-2"
              onClick={handleClick_add}
              disabled={status.isReadOnly}
            >
              <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
              Add...
            </button>
          </div>
        </Collapse>
      </div>
      {showTaSelectionModal && (
        <AdrTentativeAgreementSelectionModalV3
          tabId={tabId}
          projectId={projectId}
          createTa={createTa}
          showTaSelectionModal={showTaSelectionModal}
          toggleTaSelectionModal={toggleTaSelectionModal}
        />
      )}
      {showTaModal && (
        <AdrTentativeAgreementModalV3
          tabId={tabId}
          projectId={projectId}
          status={status}
          clientId={clientId.value}
          isNew={clientId.isNew}
          markAdrClean={markAdrClean}
          showTaModal={showTaModal}
          toggleTaModal={toggleTaModal}
        />
      )}
      {alertState_deleteConfirm.showAlert && (
        <AlertModal
          alertKey={"taDeleteConfirm"}
          alertTitle={"Confirm Delete"}
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="Are you sure you want to delete this TA?"
          acceptCaption={"Yes"}
          acceptClass={"danger"}
          provideReject={true}
          rejectCaption={"No"}
          rejectClass={"secondary"}
          handleAlertAction={handleAlert_deleteConfirm}
          showAlertModal={alertState_deleteConfirm.showAlert}
          toggleAlertModal={toggleAlert_deleteConfirm}
        />
      )}
    </div>
  );
}
